import { useEffect, useState } from 'react';

import { fetchDomainScan } from '/b2b/domains/services';

// A scan's full detail — the resolved s3 detail for *every* check (reject-all + GPC alike) —
// comes back in a single GET /scan/{id} response. Cache the in-flight promise per scanId so the
// fetch fires exactly once no matter how many detail panels open it, or how often a panel is
// collapsed and re-expanded (the grid unmounts the panel on collapse, so each re-open would
// otherwise re-download). The cache lives for the page session; a scanId is unique per scan, so
// there's no staleness to invalidate.
const scanDetailCache = new Map();

const cacheKey = (scanId, params) =>
    params && Object.keys(params).length ? `${scanId}:${JSON.stringify(params)}` : `${scanId}`;

export const fetchScanDetailOnce = (scanId, params) => {
    const key = cacheKey(scanId, params);

    if (!scanDetailCache.has(key)) {
        scanDetailCache.set(
            key,
            // Evict a rejected request so a later open can retry instead of caching the failure.
            fetchDomainScan(scanId, params).catch(error => {
                scanDetailCache.delete(key);
                throw error;
            })
        );
    }

    return scanDetailCache.get(key);
};

// Test seam: lets unit tests reset the module-level cache between cases.
export const __clearScanDetailCache = () => scanDetailCache.clear();

/**
 * Resolves the full scan (with every check's downloaded detail) for `scanId`, sharing one request
 * across every detail panel via {@link fetchScanDetailOnce}. The cache is keyed by scanId + params,
 * so callers with different query params get independent cached responses.
 *
 * @param {number|string|null|undefined} scanId
 * @param {object} [params] - Optional query params forwarded to fetchDomainScan (e.g. { excludeChecks })
 * @returns {{ scan: object|null, isLoading: boolean }}
 */
const useScanDetail = (scanId, params) => {
    const [scan, setScan] = useState(null);
    const [isLoading, setIsLoading] = useState(Boolean(scanId));

    useEffect(() => {
        if (!scanId) {
            setScan(null);
            setIsLoading(false);
            return undefined;
        }

        let cancelled = false;
        setIsLoading(true);

        fetchScanDetailOnce(scanId, params).then(
            result => {
                if (!cancelled) {
                    setScan(result);
                    setIsLoading(false);
                }
            },
            () => {
                if (!cancelled) {
                    setScan(null);
                    setIsLoading(false);
                }
            }
        );

        return () => {
            cancelled = true;
        };
    }, [scanId, params]);

    return { scan, isLoading };
};

export default useScanDetail;
